<script lang="ts">
import type { ComponentSize, FAFontSize, FAIconPack, UIProp } from '~/types/elementsUi'
import { twMerge } from '~/utils/tw-utils'
import { textColorMap } from '~/models/elementsUi'
import type { FAIconName } from '#shared/types/generated-icons'
import type {
  BaseTailwindColor,
  ExtendedTailwindColor,
  TailwindColor
} from '#shared/types/elementsUi'

export interface VIconProps {
  icon: FAIconName
  iconPack?: FAIconPack
  size?: ComponentSize | 'tiny'
  fontAwesomeIconSize?: FAFontSize
  color?: ExtendedTailwindColor
  darkColor?: ExtendedTailwindColor
  backgroundColor?: TailwindColor
  text?: string
  disableIconClass?: boolean
  overrideClickable?: boolean
  forceClickable?: boolean
  forceColor?: boolean
  ariaLabel?: string

  ui?: UIProp<'icon'>
}
</script>

<script setup lang="ts">
import VHtmlButton from '~/components/atoms/button/VHtmlButton.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const props = withDefaults(defineProps<VIconProps>(), {
  iconPack: 'fas',
  size: undefined,
  fontAwesomeIconSize: undefined,
  color: undefined,
  darkColor: undefined,
  text: undefined,
  backgroundColor: undefined,
  ui: undefined,
  ariaLabel: undefined
})

const { t, te } = useI18n()

const isFontAwesome = computed(() => props.icon.startsWith('fa-'))

const computedIcon = computed(() => {
  if (isFontAwesome.value && props.icon !== 'fa-spinner fa-pulse') {
    return [props.iconPack, props.icon]
  } else {
    return props.icon
  }
})

const computedIconSize = computed(() => {
  if (props.size) {
    return props.size
  }
  return 'normal'
})

const computedFontAwesomeIconSize = computed(() => {
  if (props.fontAwesomeIconSize) {
    return `fa-${props.fontAwesomeIconSize}`
  } else if (props.size == 'large') {
    return 'fa-2x'
  } else if (props.size == 'medium') {
    return 'fa-lg'
  }
  return undefined
})

const computedIconColor = computed(() => {
  let color = undefined

  if (props.color) {
    if (props.forceColor) {
      if (!(props.color in textColorMap)) {
        color = `text-${props.color}`
      } else {
        color = textColorMap[props.color as BaseTailwindColor]
      }
    } else {
      color = `text-${props.color}`
    }
  }

  if (props.darkColor) {
    color = twMerge(color, `dark:text-${props.darkColor}`)
  }

  return color
})

const computedBackgroundColor = computed(() => {
  if (props.backgroundColor) {
    return twMerge(
      `bg-${props.backgroundColor} text-${props.backgroundColor}-text`,
      props.color ? `text-${props.color}` : ''
    )
  }
  return undefined
})

const slots = useSlots()
const attrs = useAttrs()
const isButton = computed(
  () => (!!attrs.onClick && !props.overrideClickable) || props.forceClickable
)
const computedClickable = computed(() => isButton.value && 'cursor-pointer')

const computedAriaLabel = computed(() =>
  props.ariaLabel && te(props.ariaLabel) ? t(props.ariaLabel) : props.ariaLabel
)

const computedClass = computed(() => [
  props.iconPack,
  props.icon,
  props.ui?.icon,
  computedIconColor.value,
  computedFontAwesomeIconSize.value,
  computedClickable.value
])

const outerComponent = computed(() => {
  if (isButton.value) {
    return VHtmlButton
  } else if (props.text || slots['text']) {
    return 'div'
  } else {
    return 'span'
  }
})
</script>

<template>
  <component
    :is="outerComponent"
    v-if="text || $slots['text']"
    class="icon-text"
    :class="computedClickable"
    :aria-label="computedAriaLabel"
  >
    <span
      :class="[!disableIconClass && 'icon', computedIconSize, computedBackgroundColor]"
    >
      <FontAwesomeIcon v-if="isFontAwesome" :icon="computedIcon" :class="computedClass" />
      <i v-else :class="computedClass" />
    </span>
    <slot name="text">
      <span class="ml-1">
        {{ text }}
      </span>
    </slot>
  </component>
  <component
    v-else
    :is="outerComponent"
    :aria-label="computedAriaLabel"
    :role="!isButton && ariaLabel ? 'img' : undefined"
    :class="[
      !disableIconClass && 'icon',
      computedIconSize,
      computedClickable,
      computedBackgroundColor
    ]"
  >
    <FontAwesomeIcon v-if="isFontAwesome" :icon="computedIcon" :class="computedClass" />
    <i v-else :class="computedClass" />
  </component>
</template>

<style scoped></style>
